import React from 'react';
import { createPortal } from 'react-dom';
import styles from './Tooltip.module.css';

type Placement = 'top' | 'bottom' | 'left' | 'right';

interface TooltipProps {
  content: React.ReactNode;
  placement?: Placement;
  /** Retardo antes de mostrar (ms) */
  delay?: number;
  children: React.ReactElement;
  disabled?: boolean;
  /** Clase extra para el contenedor (p. ej. para ocupar todo el ancho) */
  className?: string;
  /** Permite texto largo en varias líneas con ancho máximo (para explicaciones). */
  multiline?: boolean;
}

interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Tooltip propio con animación de aparición. El contenido se renderiza en un
 * portal (position: fixed) que replica el rect del elemento ancla, de modo que
 * nunca queda tapado por otras tarjetas/stacking contexts.
 */
const Tooltip: React.FC<TooltipProps> = ({
  content,
  placement = 'top',
  delay = 120,
  children,
  disabled,
  className,
  multiline,
}) => {
  const [open, setOpen] = React.useState(false);
  const [rect, setRect] = React.useState<Rect | null>(null);
  const wrapRef = React.useRef<HTMLSpanElement>(null);
  const timer = React.useRef<number | undefined>(undefined);

  const measure = React.useCallback(() => {
    const r = wrapRef.current?.getBoundingClientRect();
    if (r) setRect({ left: r.left, top: r.top, width: r.width, height: r.height });
  }, []);

  const show = () => {
    if (disabled) return;
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      measure();
      setOpen(true);
    }, delay);
  };
  const hide = () => {
    window.clearTimeout(timer.current);
    setOpen(false);
  };

  React.useEffect(() => () => window.clearTimeout(timer.current), []);

  React.useEffect(() => {
    if (!open) return;
    const onMove = () => measure();
    window.addEventListener('scroll', onMove, true);
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, true);
      window.removeEventListener('resize', onMove);
    };
  }, [open, measure]);

  return (
    <span
      ref={wrapRef}
      className={[styles.wrap, className || ''].filter(Boolean).join(' ')}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {open && !disabled && rect &&
        createPortal(
          <span
            className={styles.anchor}
            style={{ left: rect.left, top: rect.top, width: rect.width, height: rect.height }}
          >
            <span
              className={[styles.tip, styles[placement], multiline ? styles.multiline : '']
                .filter(Boolean)
                .join(' ')}
              role="tooltip"
            >
              {content}
              <span className={styles.arrow} />
            </span>
          </span>,
          document.body
        )}
    </span>
  );
};

export default Tooltip;
